import { Link, useParams } from "@tanstack/solid-router";
import { Show, createEffect, createSignal } from "solid-js";

import { AccountPicker } from "~/components/AccountPicker";
import { AppShell } from "~/components/AppShell";
import { ContactAccountsCard } from "~/components/ContactAccountsCard";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { DealsForEntityCard } from "~/components/DealsForEntityCard";
import { EntityTimeline } from "~/components/EntityTimeline";
import { LifecycleProgress } from "~/components/LifecycleProgress";
import { MergeContactDialog } from "~/components/MergeContactDialog";
import { recordView } from "~/lib/recentViews";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import { BriefModal } from "~/components/BriefModal";
import { DraftEmailModal } from "~/components/DraftEmailModal";
import { LeadScoreBadge } from "~/components/LeadScoreBadge";
import { UserPicker } from "~/components/UserPicker";
import {
  useContact,
  useContactAccounts,
  useGenerateContactBrief,
  useScoreLead,
  useUpdateContact,
  useWorkspaceUser,
} from "~/lib/hooks";
import type { BriefResponse } from "~/lib/types";

export function ContactDetailPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string; conId: string };

  const contact = useContact(
    () => params().wsKey,
    () => params().conId,
  );
  const links = useContactAccounts(
    () => params().wsKey,
    () => params().conId,
  );
  const updateMutation = useUpdateContact(() => params().wsKey);

  const [editing, setEditing] = createSignal(false);
  const [showMerge, setShowMerge] = createSignal(false);
  const briefMutation = useGenerateContactBrief(() => params().wsKey);
  const [showBrief, setShowBrief] = createSignal(false);
  const [briefError, setBriefError] = createSignal<string | null>(null);
  const [briefResult, setBriefResult] = createSignal<BriefResponse | null>(null);
  const runBrief = async () => {
    setShowBrief(true);
    setBriefError(null);
    setBriefResult(null);
    try {
      const r = await briefMutation.mutateAsync({ conId: params().conId });
      setBriefResult(r);
    } catch (err) {
      setBriefError((err as Error).message);
    }
  };
  const [showDraftEmail, setShowDraftEmail] = createSignal(false);
  const scoreMut = useScoreLead(() => params().wsKey);
  const [scoreError, setScoreError] = createSignal<string | null>(null);
  const runScore = async () => {
    setScoreError(null);
    try {
      await scoreMut.mutateAsync({ conId: params().conId });
    } catch (err) {
      setScoreError((err as Error).message);
    }
  };
  const [formError, setFormError] = createSignal<string | null>(null);
  const [customDraft, setCustomDraft] = createSignal<Record<string, unknown>>({});
  const [primaryAccountDraft, setPrimaryAccountDraft] = createSignal<string | null>(null);
  const [primaryAccountNameDraft, setPrimaryAccountNameDraft] = createSignal<string | null>(null);
  const [tagsDraft, setTagsDraft] = createSignal<string[]>([]);
  const [ownerDraft, setOwnerDraft] = createSignal<string | null>(null);
  const [ownerNameDraft, setOwnerNameDraft] = createSignal<string | null>(null);

  const owner = useWorkspaceUser(
    () => params().wsKey,
    () => contact.data?.owner_user_id ?? null,
  );

  // Look up the primary link to label the chip in view + edit modes.
  const primaryLink = () => links.data?.items.find((l) => l.is_primary) ?? null;

  createEffect(() => {
    if (contact.data) setFormError(null);
  });

  createEffect(() => {
    const c = contact.data;
    if (!c) return;
    recordView(params().wsKey, {
      entity_type: "contact",
      id: c.id,
      name: c.display_name ?? c.full_name ?? c.primary_email ?? "Unnamed",
      subtitle: c.primary_email ?? c.lifecycle_stage,
    });
  });

  createEffect(() => {
    if (editing() && contact.data) {
      setCustomDraft({ ...(contact.data.custom_fields ?? {}) });
      setPrimaryAccountDraft(contact.data.primary_account_id);
      setPrimaryAccountNameDraft(primaryLink()?.account_name ?? null);
      setTagsDraft([...(contact.data.tags ?? [])]);
      setOwnerDraft(contact.data.owner_user_id ?? null);
      setOwnerNameDraft(owner.data ? (owner.data.display_name ?? owner.data.email) : null);
    }
  });

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!contact.data) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    try {
      // Send primary_account_id only when the user actually changed it, so
      // the backend's exclude_unset path can tell "leave as is" from
      // "explicitly clear to null".
      const body: Record<string, unknown> = {
        first_name: (String(data.get("first_name") ?? "") || null) as string | null,
        last_name: (String(data.get("last_name") ?? "") || null) as string | null,
        display_name: (String(data.get("display_name") ?? "") || null) as string | null,
        primary_email: (String(data.get("primary_email") ?? "") || null) as string | null,
        primary_phone: (String(data.get("primary_phone") ?? "") || null) as string | null,
        title: (String(data.get("title") ?? "") || null) as string | null,
        lifecycle_stage: String(data.get("lifecycle_stage") ?? contact.data.lifecycle_stage),
        custom_fields: customDraft(),
        tags: tagsDraft(),
      };
      if (primaryAccountDraft() !== contact.data.primary_account_id) {
        body.primary_account_id = primaryAccountDraft();
      }
      if (ownerDraft() !== (contact.data.owner_user_id ?? null)) {
        body.owner_user_id = ownerDraft();
      }
      await updateMutation.mutateAsync({
        conId: contact.data.id,
        version: contact.data.version,
        body,
      });
      setEditing(false);
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === "OptimisticLockError") {
        setFormError(
          "This contact was changed by someone else. Reload to see the latest, then re-apply your edits.",
        );
      } else {
        setFormError(e.message);
      }
    }
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Contact"
      rightSlot={
        <Link
          to="/workspaces/$wsKey/contacts"
          params={{ wsKey: params().wsKey }}
          class="btn btn-ghost btn-sm"
        >
          ← Back to list
        </Link>
      }
    >
      <Show when={contact.isLoading}>
        <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
          Loading…
        </div>
      </Show>
      <Show when={contact.isError}>
        <div role="alert" class="alert alert-error">
          Failed to load contact.
        </div>
      </Show>
      <Show when={contact.data}>
        {(data) => (
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <div class="flex items-start justify-between">
                <div>
                  <h2 class="card-title flex items-center gap-2">
                    {data().display_name ?? data().full_name}
                    <LeadScoreBadge score={data().lead_score} />
                  </h2>
                  <div class="flex gap-2 mt-1">
                    <span class="badge badge-outline badge-sm">{data().lifecycle_stage}</span>
                    <Show when={data().do_not_contact}>
                      <span
                        class="badge badge-error badge-sm"
                        title={data().do_not_contact_reason ?? undefined}
                      >
                        Do not contact
                      </span>
                    </Show>
                  </div>
                  <Show when={data().lead_score_reasoning}>
                    <p class="text-xs opacity-70 mt-2 max-w-2xl">
                      <span class="opacity-50">AI score:</span> {data().lead_score_reasoning}
                      <Show when={data().lead_score_at}>
                        <span class="opacity-50">
                          {" "}
                          · scored {new Date(data().lead_score_at!).toLocaleDateString()}
                        </span>
                      </Show>
                    </p>
                  </Show>
                </div>
                <Show when={!editing()}>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={runBrief}
                      disabled={briefMutation.isPending}
                      aria-label="Generate an AI brief about this contact"
                    >
                      {briefMutation.isPending ? "Generating…" : "✨ Generate brief"}
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={runScore}
                      disabled={scoreMut.isPending}
                      aria-label="Score this lead with AI"
                      title="Compute or refresh the AI lead score"
                    >
                      {scoreMut.isPending
                        ? "Scoring…"
                        : data().lead_score === null
                          ? "🎯 Score lead"
                          : "🎯 Re-score"}
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={() => setShowDraftEmail(true)}
                      disabled={contact.data?.do_not_contact === true}
                      title={
                        contact.data?.do_not_contact
                          ? "This contact is marked do-not-contact"
                          : undefined
                      }
                      aria-label="Draft an email to this contact"
                    >
                      ✉️ Draft email
                    </button>
                    <button class="btn btn-ghost btn-sm" onClick={() => setShowMerge(true)}>
                      Merge…
                    </button>
                    <button class="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                  </div>
                </Show>
              </div>

              <Show when={scoreError()}>
                <div role="alert" class="alert alert-error text-sm mt-2">
                  {scoreError()}
                </div>
              </Show>

              <Show when={!editing()}>
                <div class="mt-4">
                  <LifecycleProgress
                    stages={[
                      "lead",
                      "marketing_qualified",
                      "sales_qualified",
                      "opportunity",
                      "customer",
                      "evangelist",
                      "churned",
                    ]}
                    terminalStages={["churned"]}
                    formatStage={(s) =>
                      s === "marketing_qualified"
                        ? "MQL"
                        : s === "sales_qualified"
                          ? "SQL"
                          : s.charAt(0).toUpperCase() + s.slice(1)
                    }
                    current={data().lifecycle_stage}
                    onSelect={async (stage) => {
                      if (stage === data().lifecycle_stage) return;
                      await updateMutation.mutateAsync({
                        conId: data().id,
                        version: data().version,
                        body: { lifecycle_stage: stage },
                      });
                    }}
                  />
                </div>
                <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4">
                  <DetailRow label="First name" value={data().first_name} />
                  <DetailRow label="Last name" value={data().last_name} />
                  <DetailRow label="Email" value={data().primary_email} />
                  <DetailRow label="Phone" value={data().primary_phone} />
                  <DetailRow label="Title" value={data().title} />
                  <DetailRow label="Department" value={data().department} />
                  <DetailRow
                    label="Primary account"
                    value={primaryLink()?.account_name ?? (data().primary_account_id ? "—" : null)}
                  />
                  <DetailRow
                    label="Owner"
                    value={
                      owner.data
                        ? (owner.data.display_name ?? owner.data.email)
                        : data().owner_user_id
                          ? "—"
                          : null
                    }
                  />
                  <DetailRow label="Subscription" value={data().email_subscription_state} />
                  <DetailRow label="Created" value={new Date(data().created_at).toLocaleString()} />
                  <DetailRow label="Updated" value={new Date(data().updated_at).toLocaleString()} />
                  <DetailRow label="Version" value={String(data().version)} />
                </dl>
                <Show when={(data().tags?.length ?? 0) > 0}>
                  <div class="mt-4">
                    <div class="text-xs uppercase opacity-50 mb-1">Tags</div>
                    <TagBadges tags={() => data().tags} />
                  </div>
                </Show>
                <div class="mt-4">
                  <CustomFieldsRenderer
                    wsKey={() => params().wsKey}
                    entityType="contact"
                    values={() => data().custom_fields}
                    onChange={() => {
                      /* read-only in view mode */
                    }}
                    variant="view"
                  />
                </div>
              </Show>

              <Show when={editing()}>
                <form onSubmit={handleSubmit} class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <LabeledInput
                    name="first_name"
                    label="First name"
                    value={data().first_name ?? ""}
                  />
                  <LabeledInput name="last_name" label="Last name" value={data().last_name ?? ""} />
                  <LabeledInput
                    name="display_name"
                    label="Display name (override)"
                    value={data().display_name ?? ""}
                  />
                  <LabeledInput name="title" label="Title" value={data().title ?? ""} />
                  <LabeledInput
                    name="primary_email"
                    label="Email"
                    type="email"
                    value={data().primary_email ?? ""}
                  />
                  <LabeledInput
                    name="primary_phone"
                    label="Phone"
                    value={data().primary_phone ?? ""}
                  />
                  <LabeledSelect
                    name="lifecycle_stage"
                    label="Lifecycle stage"
                    value={data().lifecycle_stage}
                    options={[
                      "lead",
                      "marketing_qualified",
                      "sales_qualified",
                      "opportunity",
                      "customer",
                      "evangelist",
                      "churned",
                    ]}
                  />
                  <div>
                    <label class="label" for="contact-field-primary-account">
                      <span class="label-text">Primary account</span>
                    </label>
                    <AccountPicker
                      wsKey={() => params().wsKey}
                      value={primaryAccountDraft}
                      selectedName={primaryAccountNameDraft}
                      onChange={(id, name) => {
                        setPrimaryAccountDraft(id);
                        setPrimaryAccountNameDraft(name);
                      }}
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label class="label">
                      <span class="label-text">Tags</span>
                    </label>
                    <TagsEditor
                      wsKey={() => params().wsKey}
                      entityType="contact"
                      value={tagsDraft}
                      onChange={setTagsDraft}
                    />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Owner</span>
                    </label>
                    <UserPicker
                      wsKey={() => params().wsKey}
                      value={ownerDraft}
                      selectedName={ownerNameDraft}
                      onChange={(id, name) => {
                        setOwnerDraft(id);
                        setOwnerNameDraft(name);
                      }}
                    />
                  </div>
                  <div class="md:col-span-2">
                    <CustomFieldsRenderer
                      wsKey={() => params().wsKey}
                      entityType="contact"
                      values={() => customDraft()}
                      onChange={(key, value) =>
                        setCustomDraft((prev) => ({ ...prev, [key]: value }))
                      }
                    />
                  </div>
                  <Show when={formError()}>
                    <div class="md:col-span-2 alert alert-error text-sm">{formError()}</div>
                  </Show>
                  <div class="md:col-span-2 flex gap-2 justify-end">
                    <button type="button" class="btn btn-ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              </Show>
            </div>
          </div>
        )}
      </Show>

      <Show when={contact.data}>
        {(data) => <ContactAccountsCard wsKey={() => params().wsKey} contactId={() => data().id} />}
      </Show>

      <Show when={contact.data}>
        {(data) => (
          <DealsForEntityCard
            wsKey={() => params().wsKey}
            entityType="contact"
            entityId={() => data().id}
          />
        )}
      </Show>

      <Show when={contact.data}>
        {(data) => (
          <EntityTimeline
            wsId={() => params().wsKey}
            entityType="contact"
            entityId={() => data().id}
          />
        )}
      </Show>

      <Show when={showMerge()}>
        <Show when={contact.data}>
          {(data) => (
            <MergeContactDialog
              wsKey={() => params().wsKey}
              doomed={data()}
              onClose={() => setShowMerge(false)}
            />
          )}
        </Show>
      </Show>

      <Show when={showBrief()}>
        <BriefModal
          subjectLabel={contact.data?.full_name ?? contact.data?.display_name ?? "this contact"}
          pending={briefMutation.isPending}
          error={briefError()}
          result={briefResult()}
          onClose={() => setShowBrief(false)}
          onRegenerate={runBrief}
        />
      </Show>

      <Show when={showDraftEmail()}>
        <DraftEmailModal
          wsKey={() => params().wsKey}
          conId={() => params().conId}
          contactLabel={() =>
            contact.data?.full_name ?? contact.data?.display_name ?? "this contact"
          }
          onClose={() => setShowDraftEmail(false)}
        />
      </Show>
    </AppShell>
  );
}

function DetailRow(props: { label: string; value: string | null }) {
  return (
    <div>
      <dt class="text-xs uppercase opacity-50">{props.label}</dt>
      <dd class="font-medium">{props.value ?? "—"}</dd>
    </div>
  );
}

function LabeledInput(props: {
  name: string;
  label: string;
  value: string;
  type?: string;
}) {
  const id = `contact-field-${props.name}`;
  return (
    <div>
      <label for={id} class="label">
        <span class="label-text">{props.label}</span>
      </label>
      <input
        id={id}
        name={props.name}
        class="input input-bordered w-full"
        value={props.value}
        type={props.type ?? "text"}
      />
    </div>
  );
}

function LabeledSelect(props: {
  name: string;
  label: string;
  value: string;
  options: string[];
}) {
  const id = `contact-field-${props.name}`;
  return (
    <div>
      <label for={id} class="label">
        <span class="label-text">{props.label}</span>
      </label>
      <select id={id} name={props.name} class="select select-bordered w-full">
        {props.options.map((o) => (
          <option value={o} selected={o === props.value}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
